@use "../../../scss/settings/colors";
@use "../../../scss/settings/breakpoints";

// Customers / Suppliers list blocks. Same look as the Document Vault block
// (pages/document-vault/scss/DocumentVault.module.scss) and its compact table
// (pages/document-vault/components/document-list-vault/DocumentListVault.module.scss).
$block-radius: 16px;
$control-height: 40px;
$switcher-bg: #eef0f4;
$active-bg: colors.$light-purple;

// Category accents of the icon tiles (same as the category cards).
$accent-purple-bg: #f3ebff;
$accent-blue-bg: #e8f2fd;
$accent-green: #10a36d;
$accent-green-bg: #e7f8f0;
$accent-amber: #a8792c;
$accent-amber-bg: #fdf3e2;

// ---------- Block: actions header + table / cards ----------
.profiles_block {
  position: relative;
  width: 100%;
  min-width: 0;

  // Card view: the actions header is a rounded block of its own above the cards.
  .block_header {
    margin-bottom: 15px;
    border: 1px solid colors.$gray-variant-4;
    border-radius: $block-radius;
  }
}

// List view: the actions header and the table form one rounded block.
.profiles_block_joined {
  overflow: hidden;
  background-color: colors.$base-white;
  border: 1px solid colors.$gray-variant-4;
  border-radius: $block-radius;

  .block_header {
    margin-bottom: 0;
    border: none;
    border-bottom: 1px solid colors.$gray-variant-4;
    border-radius: 0;
  }
}

.block_content {
  position: relative;
  min-height: 80px;
}

.block_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px;
  background-color: colors.$base-white;
}

// "Add profile" button: same height as the search and the switcher.
.block_header .add_profile_button:global(.MuiButton-root) {
  flex-shrink: 0;
  height: $control-height;
  padding: 0 16px;
  font-family: 'Medium Inter', sans-serif;
  font-size: 14px;
  line-height: 1;
  text-transform: none;
  white-space: nowrap;
  color: colors.$base-white;
  background-color: colors.$brand-purple;
  border: 1px solid colors.$brand-purple;
  border-radius: 10px;
  box-shadow: none;
  transition: color 0.2s, background-color 0.2s;

  &:hover {
    color: colors.$brand-purple;
    background-color: colors.$base-white;
    box-shadow: none;
  }

  &:focus-visible {
    outline: 2px solid colors.$brand-purple;
    outline-offset: 1px;
  }
}

// Search. The extra class outranks the global `.updated-variant .MuiInputBase-root` /
// `.search-input` height rules (scss/common/_form.scss).
.block_header .search:global(.MuiTextField-root) {
  flex: 1 1 260px;
  min-width: 0;
  max-width: 400px;

  :global(.MuiInputBase-root.MuiOutlinedInput-root) {
    height: $control-height !important;
    padding-left: 12px;
    padding-right: 6px;
    font-family: 'Regular Inter', sans-serif;
    font-size: 14px;
    color: colors.$dark-blue;
    background-color: colors.$base-white;
    border-radius: 10px !important;

    :global(.MuiOutlinedInput-notchedOutline) {
      border-color: colors.$gray-variant-4;
    }

    &:hover:not(:global(.Mui-disabled)) :global(.MuiOutlinedInput-notchedOutline) {
      border-color: colors.$border-grey;
    }

    &:global(.Mui-focused) :global(.MuiOutlinedInput-notchedOutline) {
      border-width: 1px;
      border-color: colors.$brand-purple;
    }
  }

  :global(.MuiInputBase-input) {
    height: $control-height !important;
    padding: 0;
    box-sizing: border-box;

    &::placeholder {
      color: colors.$gray-variant-16;
      opacity: 1;
    }
  }

  :global(.MuiInputAdornment-positionStart) {
    margin-right: 8px;
  }
}

.search_icon:global(.MuiSvgIcon-root) {
  width: 18px;
  height: 18px;
  color: colors.$gray-variant-16;
}

// Pagination + view switcher on the right of the header.
.header_controls {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-left: auto;
}

.header_pagination {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.pages_value:global(.MuiTypography-root) {
  font-family: 'Medium Inter', sans-serif;
  font-size: 0.8125rem;
  color: colors.$gray-variant-16;
}

.pagination:global(.MuiPagination-root) {

  :global(.MuiPagination-ul) {
    gap: 4px;
  }

  :global(.MuiPaginationItem-root) {
    min-width: 32px;
    height: 32px;
    margin: 0;
    font-family: 'Medium Inter', sans-serif;
    font-size: 0.8125rem;
    color: colors.$dark-blue;
    border-radius: 8px;

    &:global(.Mui-selected) {
      color: colors.$brand-purple;
      background-color: $active-bg;
    }
  }
}

// Card / list switcher: icon-only segmented control.
.card_list_view_container {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 2px;
  height: $control-height;
  padding: 3px;
  background-color: $switcher-bg;
  border-radius: 10px;
}

.variant_btn {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 34px;
  height: 34px;
  padding: 0;
  color: colors.$dark-blue;
  background-color: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: color 0.2s, background-color 0.2s, box-shadow 0.2s;

  &[disabled] {
    opacity: 0.5;
  }

  &:hover:not([disabled]) {
    color: colors.$brand-purple;
  }

  &.variant_btn_active:not([disabled]) {
    color: colors.$brand-purple;
    background-color: colors.$base-white;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.12);
  }

  &:focus-visible {
    outline: 2px solid colors.$brand-purple;
    outline-offset: 1px;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: currentColor !important;
  }
}

.visually_hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

// ---------- Table (Document Vault compact rows) ----------
// Scoped to the Paper root so it outranks the MUI Paper default radius; the block draws the border.
.table_container:global(.MuiPaper-root) {
  background-color: colors.$base-white !important;
  border-radius: 0;
}

.table {
  border-collapse: separate;
}

.empty_cell:global(.MuiTableCell-root) {
  border-bottom: none;
}

.empty_message {
  max-width: 260px;
  margin-left: auto;
  margin-right: auto;
  font-family: "Light Inter", sans-serif;
  font-style: italic;
  font-size: 0.875rem;
  line-height: 1.3125rem;
  text-align: center;
  color: colors.$dark-blue;
}

.row {
  cursor: pointer;
  transition: background-color 0.2s;

  :global(.MuiTableCell-root) {
    border-bottom: 1px solid colors.$gray-variant-4;
  }

  &:last-child :global(.MuiTableCell-root) {
    border-bottom: none;
  }

  &:hover {
    background-color: colors.$gray-variant-20;
  }
}

.cell:global(.MuiTableCell-root) {
  padding: 12px 16px;
  vertical-align: middle;
}

// The name column takes the free width; the actions column shrinks to fit.
.name_cell {
  width: 100%;
  min-width: 260px;
}

.actions_cell {
  width: 1%;
  white-space: nowrap;
  cursor: default;
}

.name_container {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

// Icon tile at the start of the row (same place and size as the Vault file type badge).
.icon_tile {
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  color: colors.$dark-blue;
  background-color: colors.$gray-variant-20;
  border-radius: 10px;

  :global(.MuiSvgIcon-root) {
    width: 20px;
    height: 20px;
  }

  &.accent_purple {
    color: colors.$brand-purple;
    background-color: $accent-purple-bg;
  }

  &.accent_blue {
    color: colors.$brand-blue;
    background-color: $accent-blue-bg;
  }

  &.accent_green {
    color: $accent-green;
    background-color: $accent-green-bg;
  }

  &.accent_amber {
    color: $accent-amber;
    background-color: $accent-amber-bg;
  }
}

.text_container {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.title:global(.MuiTypography-root) {
  font-family: 'Bold Inter', sans-serif !important;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: colors.$dark-blue;
  overflow-wrap: anywhere;
}

.subtitle:global(.MuiTypography-root) {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 16px;
  row-gap: 2px;
  font-family: 'Regular Inter', sans-serif;
  font-size: 0.75rem;
  line-height: 1.125rem;
  color: colors.$gray-variant-16;
}

.detail {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.detail_icon {
  display: inline-flex;
  flex-shrink: 0;
  font-size: 14px;
}

.detail_text {
  overflow-wrap: anywhere;
}

.actions_container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding-left: 8px;
}

.action_button:global(.MuiIconButton-root) {
  flex-shrink: 0;
  color: colors.$dark-blue;

  svg {
    fill: colors.$dark-blue !important;
  }
}

// ---------- Responsive ----------
// Mobile: the controls take a line of their own under the search, the switcher stays on the right.
.block_header .search:global(.MuiTextField-root) {

  @media #{breakpoints.$mobile}{
    flex-basis: 100%;
    max-width: none;
  }

}

.header_controls {

  @media #{breakpoints.$mobile}{
    justify-content: space-between;
    width: 100%;
    gap: 8px;
  }

}

.header_controls .card_list_view_container {

  @media #{breakpoints.$mobile}{
    margin-left: auto;
  }

}

.cell:global(.MuiTableCell-root) {

  @media #{breakpoints.$mobile}{
    padding: 12px;
  }

}

.name_cell {

  @media #{breakpoints.$mobile}{
    min-width: 220px;
  }

}
